import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { getService } from "./service";

const initialState = {
  trainingCosts: [],
  costsByParticipant: {},
  loading: false,
  error: null,
  total: 0,
};

const FETCH_REQUEST = "FETCH_COST_REQUEST";
const FETCH_SUCCESS = "FETCH_COST_SUCCESS";
const FETCH_FAILURE = "FETCH_COST_FAILURE";
const CREATE_REQUEST = "CREATE_COST_REQUEST";
const CREATE_SUCCESS = "CREATE_COST_SUCCESS";
const CREATE_FAILURE = "CREATE_COST_FAILURE";
const UPDATE_REQUEST = "UPDATE_COST_REQUEST";
const UPDATE_SUCCESS = "UPDATE_COST_SUCCESS";
const UPDATE_FAILURE = "UPDATE_COST_FAILURE";
const DELETE_REQUEST = "DELETE_COST_REQUEST";
const DELETE_SUCCESS = "DELETE_COST_SUCCESS";
const DELETE_FAILURE = "DELETE_COST_FAILURE";
const FETCH_BY_PARTICIPANT_REQUEST = "FETCH_BY_PARTICIPANT_REQUEST";
const FETCH_BY_PARTICIPANT_SUCCESS = "FETCH_BY_PARTICIPANT_SUCCESS";
const FETCH_BY_PARTICIPANT_FAILURE = "FETCH_BY_PARTICIPANT_FAILURE";

export const getTrainingCosts =
  (
    page = 0,
    pageSize = 5,
    sortField = "id",
    sortDirection = "asc",
    filters = {}
  ) =>
  async (dispatch) => {
    const service = getService(serviceNames.perTrainingCost);
    const query = {
      $limit: pageSize,
      $skip: page * pageSize,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      isDeleted: false,
    };

    const textFields = [];

    textFields.forEach((field) => {
      const value = filters[field];
      if (value) {
        query[field] = filters.exactMatch ? value : { $like: `%${value}%` };
      }
    });

    const getQueryValue = (input) => {
      if (Array.isArray(input)) {
        const values = input.map((i) => i.value).filter(Boolean);
        return values.length > 0 ? { $in: values } : undefined;
      } else if (input?.value) {
        return input.value;
      }
      return undefined;
    };

    const dropdownFields = [];

    dropdownFields.forEach((field) => {
      const value = getQueryValue(filters[field]);
      if (value !== undefined) {
        query[field] = value;
      }
    });

    if (filters.filterField?.value && filters.filterValue) {
      const fieldMap = {};
      const field =
        fieldMap[filters.filterField.value] || filters.filterField.value;

      query[field] = filters.exactMatch
        ? filters.filterValue
        : { $like: `%${filters.filterValue}%` };
    }

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_REQUEST,
        success: FETCH_SUCCESS,
        failure: FETCH_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (res) => {
        const costsByParticipant = res.data.reduce((acc, participant) => {
          const id = participant.perTrainingId || "Unknown";
          if (!acc[id]) {
            acc[id] = [];
          }
          acc[id].push(participant);
          return acc;
        }, {});

        return {
          trainingCosts: res.data,
          total: res.total,
          costsByParticipant,
        };
      },
      errorMessagePrefix: "Failed to fetch training costs",
    });
  };

export const createTrainingCost = (trainingCostData) => async (dispatch) => {
  const service = getService(serviceNames.perTrainingCost);
  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: CREATE_REQUEST,
      success: CREATE_SUCCESS,
      failure: CREATE_FAILURE,
    },
    apiCall: () => service.create(trainingCostData),
    successMessage: "Training Cost created successfully",
    errorMessagePrefix: "Failed to create training cost",
  });
};

export const getTrainingCostsByParticipant = (id) => async (dispatch) => {
  const service = getService(serviceNames.perTrainingCost);

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: FETCH_BY_PARTICIPANT_REQUEST,
      success: FETCH_BY_PARTICIPANT_SUCCESS,
      failure: FETCH_BY_PARTICIPANT_FAILURE,
    },
    apiCall: () =>
      service.find({
        query: {
          perTrainingId: id,
          isDeleted: false,
          $limit: -1,
        },
      }),
    transformResult: (res) => {
      return {
        id: id,
        participants: res || [],
      };
    },

    errorMessagePrefix: `Failed to fetch costs for participant ${id}`,
  });
};

export const updateTrainingCost =
  (id, trainingCostData) => async (dispatch) => {
    const service = getService(serviceNames.perTrainingCost);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_REQUEST,
        success: UPDATE_SUCCESS,
        failure: UPDATE_FAILURE,
      },
      apiCall: () => service.patch(id, trainingCostData),
      successMessage: "Training Cost updated successfully",
      errorMessagePrefix: "Failed to update training cost",
    });
  };

export const deleteTrainingCost = (id, userId) => async (dispatch) => {
  const service = getService(serviceNames.perTrainingCost);
  const patchData = {
    isDeleted: true,
    deletedBy: userId,
    deletedAt: new Date().toISOString(),
  };

  return await asyncThunkWrapper({
    dispatch,
    types: {
      request: DELETE_REQUEST,
      success: DELETE_SUCCESS,
      failure: DELETE_FAILURE,
    },
    apiCall: () => service.patch(id, patchData),
    transformResult: () => id,
    successMessage: "Training Cost deleted successfully",
    errorMessagePrefix: "Failed to delete training cost",
  });
};

export default function trainingCostReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case DELETE_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCosts: action.payload.trainingCosts,
        total: action.payload.total,
        costsByParticipant: action.payload.costsByParticipant,
      };
    case CREATE_SUCCESS:
      return {
        ...state,
        loading: false,
        trainingCosts: [...state.trainingCosts, action.payload],
        total: state.total + 1,
      };
    case UPDATE_SUCCESS: {
      const updatedTrainingCost = action.payload;

      const updatedTrainingCosts = Array.isArray(state.trainingCosts)
        ? state.trainingCosts.map((tc) =>
            tc.id === updatedTrainingCost.id ? updatedTrainingCost : tc
          )
        : [];

      const updatedCostsByParticipant = { ...state.costsByParticipant };

      Object.keys(updatedCostsByParticipant).forEach((id) => {
        const costs = updatedCostsByParticipant[id];
        if (Array.isArray(costs)) {
          updatedCostsByParticipant[id] = costs.map((cost) =>
            cost.id === updatedTrainingCost.id ? updatedTrainingCost : cost
          );
        }
      });

      return {
        ...state,
        loading: false,
        trainingCosts: updatedTrainingCosts,
        costsByParticipant: updatedCostsByParticipant,
      };
    }

    case DELETE_SUCCESS: {
      const deletedId = action.payload;

      const updatedTCs = state.trainingCosts.filter(
        (tc) => tc.id !== deletedId
      );

      const updatedCostsByParticipant = { ...state.costsByParticipant };
      Object.keys(updatedCostsByParticipant).forEach((id) => {
        updatedCostsByParticipant[id] = updatedCostsByParticipant[id].filter(
          (cost) => cost.id !== deletedId
        );
      });

      return {
        ...state,
        loading: false,
        trainingCosts: updatedTCs,
        costsByParticipant: updatedCostsByParticipant,
        total: state.total - 1,
      };
    }
    case FETCH_BY_PARTICIPANT_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_BY_PARTICIPANT_SUCCESS:
      return {
        ...state,
        loading: false,
        costsByParticipant: {
          ...state.costsByParticipant,
          [action.payload.id]: action.payload.participants,
        },
      };

    case FETCH_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case DELETE_FAILURE:
    case FETCH_BY_PARTICIPANT_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
